import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Card } from '@astryxdesign/core/Card'
import { Stack } from '@astryxdesign/core/Stack'
import { Text } from '@astryxdesign/core/Text'
import { Badge } from '@astryxdesign/core/Badge'
import { Button } from '@astryxdesign/core/Button'
import { ChatMessage } from '@astryxdesign/core/Chat'
import { ChatMessageBubble } from '@astryxdesign/core/Chat'
import { ChatToolCalls } from '@astryxdesign/core/Chat'
import { HERO_SCRIPT } from './content'
import { buildFrames, finalItems } from './heroTimeline'
import useScriptPlayer, { useIsWatched, usePrefersReducedMotion } from './useScriptPlayer'
import './hero.css'

// The hero exchange, played out: a question typed into the composer, the
// agent's tool calls landing one by one, the answer streaming in, and - in the
// second scene - a proposal the player confirms.
//
// This replaces ExampleConversation, which rendered the same exchange at rest
// on purpose. Its argument was that a page which types at you is waited for
// rather than read, and that a playing exchange edges closer to implying a
// recording. quiet-orbit-3 decided the other way: the hero's job is to show
// what the agent *does*, and the tool calls between question and answer are
// the part a static transcript cannot show. Both of the old concerns are
// still answered, just differently:
//
//  * Nobody has to wait. The loop is fast, pauses on a button, stops when the
//    card is off screen or the tab is hidden, and a reader who asked their
//    system for less motion gets the finished exchange at rest, as before.
//  * The badge and the footnote are unchanged. lively-jaguar is explicit that
//    the exchange is illustrative and must say so, because a landing page
//    showing an answer implies the app produced it.
//
// The animated region is `inert` and hidden from assistive technology - text
// rewriting itself every 30ms is noise to a screen reader - and the finished
// transcript is given to it instead, visually hidden.
export default function AnimatedConversation() {
  const frames = useMemo(() => buildFrames(HERO_SCRIPT.scenes), [])
  const atRest = useMemo(() => finalItems(HERO_SCRIPT.scenes), [])

  const ref = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const watched = useIsWatched(ref)
  const [paused, setPaused] = useState(false)
  const frame = useScriptPlayer(frames, watched && !paused && !reducedMotion)

  return (
    <Card padding={5} width="100%" maxWidth={560} ref={ref}>
      <Stack gap={4}>
        <Stack direction="horizontal" gap={2} vAlign="center" hAlign="between" wrap="wrap">
          <Text type="label" color="secondary">{HERO_SCRIPT.title}</Text>
          <Stack direction="horizontal" gap={2} vAlign="center">
            <Badge label={HERO_SCRIPT.badge} />
            {!reducedMotion && (
              <Button
                variant="ghost"
                size="sm"
                isIconOnly
                label={paused ? HERO_SCRIPT.play : HERO_SCRIPT.pause}
                icon={paused ? <PlayIcon /> : <PauseIcon />}
                onClick={() => setPaused((p) => !p)}
              />
            )}
          </Stack>
        </Stack>

        {reducedMotion ? (
          <Stack gap={3}>
            <Items items={atRest} />
          </Stack>
        ) : (
          <>
            <div className="hero-chat" aria-hidden="true" inert="">
              {/* Keyed by scene so the second scene starts from an empty card
                  rather than reusing the first scene's message elements. */}
              <div
                key={frame.scene}
                className={'hero-chat__track' + (frame.fading ? ' is-fading' : '')}
              >
                <Items items={frame.items} animated />
              </div>
            </div>
            <Composer text={frame.composer} placeholder={HERO_SCRIPT.composerPlaceholder} />
            <Transcript items={atRest} />
          </>
        )}

        <Text type="supporting">{HERO_SCRIPT.footnote}</Text>
      </Stack>
    </Card>
  )
}

function Items({ items, animated = false }) {
  const cls = animated ? 'hero-chat__item' : undefined
  return items.map((item, i) => {
    switch (item.kind) {
      case 'user':
        return (
          <div key={i} className={cls}>
            <ChatMessage sender="user">
              <ChatMessageBubble>{item.text}</ChatMessageBubble>
            </ChatMessage>
          </div>
        )
      case 'tools':
        // Expanded and controlled: collapsed, a group shows only its latest
        // call, which would hide the very sequence this is here to show. The
        // label is ours because the default is an English "2 tool calls".
        return (
          <div key={i} className={cls}>
            <ChatMessage sender="assistant">
              <div className="hero-tools">
                <ChatToolCalls
                  calls={item.calls.map(({ tool, ...call }, j) => ({ ...call, key: `${tool}-${j}` }))}
                  isExpanded
                  label={HERO_SCRIPT.toolsLabel(item.calls.length)}
                />
              </div>
            </ChatMessage>
          </div>
        )
      case 'assistant':
        return (
          <div key={i} className={cls}>
            <ChatMessage sender="assistant">
              <ChatMessageBubble>
                {item.text}
                {item.streaming && <span className="hero-caret" />}
              </ChatMessageBubble>
            </ChatMessage>
          </div>
        )
      case 'proposal':
        return (
          <div key={i} className={cls}>
            <Proposal {...item} />
          </div>
        )
      default:
        return null
    }
  })
}

// What a screen reader gets instead of the animation: the finished exchange
// as plain text. Plain, not the rendered messages, because those include the
// drawn "Confirmer" button, and a focusable button nobody can see is worse
// than none.
function Transcript({ items }) {
  return (
    <ol className="hero-visually-hidden">
      {items.map((item, i) => (
        <li key={i}>
          {item.kind === 'user' && `${HERO_SCRIPT.speakers.user} : ${item.text}`}
          {item.kind === 'assistant' && `${HERO_SCRIPT.speakers.assistant} : ${item.text}`}
          {item.kind === 'tools' &&
            `${HERO_SCRIPT.speakers.tools} : ${item.calls
              .map((c) => (c.target ? `${c.name} (${c.target})` : c.name))
              .join(', ')}`}
          {item.kind === 'proposal' &&
            `${item.proposal.title}, ${item.proposal.detail}. ${item.proposal.confirmed}`}
        </li>
      ))}
    </ol>
  )
}

// The card propose_availability puts in front of the player. Drawn rather
// than interactive - the whole region is inert - so the "tap" is a class.
function Proposal({ proposal, state }) {
  return (
    <ChatMessage sender="assistant">
      <Card padding={4} width="100%" maxWidth={320}>
        <Stack gap={3}>
          <Stack gap={1}>
            <Text weight="semibold">{proposal.title}</Text>
            <Text type="supporting">{proposal.detail}</Text>
          </Stack>
          {state === 'confirmed' ? (
            <span className="hero-confirmed">
              <Text type="label" color="inherit">✓ {proposal.confirmed}</Text>
            </span>
          ) : (
            <div className={'hero-press' + (state === 'pressing' ? ' is-pressed' : '')}>
              <Button variant="primary" size="sm" label={proposal.confirm} width="100%" />
            </div>
          )}
        </Stack>
      </Card>
    </ChatMessage>
  )
}

// A drawn composer. The question types itself in here before it is sent, so
// the reader sees it asked rather than having it appear already answered.
//
// One line, scrolled to the caret, like a real input. Left to wrap, the long
// question grew it to two or three lines on a phone and moved the whole page
// below the hero while it typed.
function Composer({ text, placeholder }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollLeft = ref.current.scrollWidth
  }, [text])
  return (
    <div className="hero-composer" aria-hidden="true" ref={ref}>
      {text ? (
        <span>
          {text}
          <span className="hero-caret" />
        </span>
      ) : (
        <span className="hero-composer__placeholder">{placeholder}</span>
      )}
    </div>
  )
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
      <rect x="3.5" y="2.5" width="3" height="11" rx="1" />
      <rect x="9.5" y="2.5" width="3" height="11" rx="1" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M4.5 2.8v10.4c0 .6.7 1 1.2.6l8-5.2a.7.7 0 0 0 0-1.2l-8-5.2c-.5-.4-1.2 0-1.2.6z" />
    </svg>
  )
}
